/**
 * EmailVerificationGate - Full-screen verification requirement gate
 *
 * Blocks unverified email/password users from accessing protected features.
 * Shows non-dismissible modal with verification prompt and resend functionality.
 * Google OAuth users bypass this entirely (they're pre-verified).
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { EnvelopeIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

export const EmailVerificationGate = () => {
  const { user, resendVerificationEmail } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResend = async () => {
    try {
      setSending(true);
      setError(null);
      await resendVerificationEmail();
      setSent(true);
      setTimeout(() => setSent(false), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to send verification email');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/95 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="max-w-md w-full mx-4"
      >
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-700/50 p-8">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center">
              <EnvelopeIcon className="w-8 h-8 text-white" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-center text-white mb-3">
            Verify Your Email
          </h2>

          {/* Message */}
          <p className="text-center text-slate-300 mb-2">
            Please verify your email address to access this feature.
          </p>
          <p className="text-center text-slate-400 text-sm mb-6">
            We sent a verification link to <span className="font-medium text-slate-200">{user?.email}</span>
          </p>

          {/* Instructions */}
          <div className="bg-slate-900/50 rounded-lg p-4 mb-6 border border-slate-700/30">
            <p className="text-sm text-slate-300 mb-2">
              Check your inbox for the verification email. Can't find it?
            </p>
            <ul className="text-sm text-slate-400 space-y-1 ml-4 list-disc">
              <li>Check your spam or junk folder</li>
              <li>Make sure you're checking {user?.email}</li>
              <li>Click the button below to resend</li>
            </ul>
          </div>

          {/* Success Message */}
          {sent && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 px-4 py-3 bg-green-500/20 border border-green-500/30 rounded-lg mb-4"
            >
              <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
              <span className="text-sm font-medium text-green-300">
                Verification email sent! Check your inbox.
              </span>
            </motion.div>
          )}

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 px-4 py-3 bg-red-500/20 border border-red-500/30 rounded-lg mb-4"
            >
              <ExclamationTriangleIcon className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-sm font-medium text-red-300">{error}</span>
            </motion.div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {/* Resend Button */}
            <button
              onClick={handleResend}
              disabled={sending || sent}
              className="w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 disabled:from-slate-600 disabled:to-slate-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 shadow-lg shadow-indigo-500/20"
            >
              {sending ? 'Sending...' : sent ? 'Email Sent!' : 'Resend Verification Email'}
            </button>

            {/* Profile Link */}
            <Link
              to="/profile"
              className="block w-full px-6 py-3 bg-slate-700/50 hover:bg-slate-700 text-slate-200 font-medium rounded-lg transition-colors text-center border border-slate-600/50"
            >
              Go to Profile
            </Link>
          </div>

          {/* Help Text */}
          <p className="text-center text-slate-500 text-xs mt-6">
            After verifying, refresh this page or navigate back to continue.
          </p>
        </div>
      </motion.div>
    </div>
  );
};
